import {
  Box,
  InputAdornment,
  OutlinedInput,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import ascendingIcon from "../../assets/images/Icons/ascending.png";
import descendingIcon from "../../assets/images/Icons/descending.png";
import emptyIcon from "../../assets/images/Icons/search-table.png";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useId, useState } from "react";
import { Loader } from "./Loader";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useDebouncedCallback } from "use-debounce";
import update from "immutability-helper";

export const CustomDndTable = (props) => {
  const {
    defaultData,
    columns,
    headerText,
    rightContent,
    subRow = null,
    // customRowStyle = null,
    tableHeaderContent = null,
    isHeaderStyleChange = false,
    isLoading = false,
    isPagination = true,
    isTableHeader = true,
    isTableHead = true,
    tableFooter,
    tableStyle,
    tableContainerStyle,
    onSearchChange,
    hideEmptyContent = false,
    updateTableData,
    disableDrag = false,
    onRowOrderChange,
    validator = null,
    searchText = null,
    maxHeight = "362px", // add maxHeight prop with default
    emptyTableMessage = null,
  } = props;

  const [records, setRecords] = React.useState([]);
  useEffect(() => {
    setRecords(defaultData || []);
  }, [defaultData]);
  const getRowId = React.useCallback((row, i) => {
    return row.rowID;
  }, []);
  const table = useReactTable({
    data: records,
    columns,
    getRowId,
    getSubRows: (row) => row[subRow] || [],
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: onSearchChange,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    meta: {
      updateData: updateTableData,
    },
    manualPagination: true,
  });
  const [state, setState] = useState(table.initialState);
  table.setOptions((prev) => ({
    ...prev,
    state,
    onStateChange: setState,
  }));

  // Unique id for this table instance to prevent cross-table drops
  const tableId = useId();

  const debouncedRowUpdate = useDebouncedCallback(
    // function
    (value) => {
      onRowOrderChange(value);
    },
    // delay in ms
    1000
  );

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRecord = records[dragIndex];
    const updatedRecords = update(records, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragRecord],
      ],
    });
    setRecords(updatedRecords);
    debouncedRowUpdate(updatedRecords);
  };

  const setGlobalFilter = React.useCallback(
    (value) => {
      setState((prevState) => ({
        ...prevState,
        globalFilter: value,
      }));

      if (onSearchChange) {
        onSearchChange(value);
      }
    },

    [onSearchChange]
  );

  return (
    <>
      {validator && validator.current.purgeFields()}
      <Stack
        sx={{
          borderRadius: "4px",
          bgcolor: "#fff",
          overflow: "hidden",
          ...tableStyle,
        }}
      >
        {isTableHeader && (
          <TableHeader
            // setGlobalFilter={(value) =>
            //   setState({ ...state, globalFilter: value })
            // }
            // setGlobalFilter={onSearchChange}
            setGlobalFilter={setGlobalFilter}
            headerText={headerText}
            rightContent={rightContent}
            isHeaderStyleChange={isHeaderStyleChange}
            searchText={searchText}
          />
        )}
        {tableHeaderContent && (
          <TableHeaderContent tableHeaderContent={tableHeaderContent} />
        )}
        <DndProvider backend={HTML5Backend}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: "0 0 4px 4px",
              overflow: "auto",
              maxHeight: isTableHead ? maxHeight : undefined, // only set maxHeight if header is present
              ...tableContainerStyle,
            }}
          >
            <Table stickyHeader={isTableHead}>
              {isTableHead && (
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {!disableDrag && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            backgroundColor: "background.default",
                          }}
                        />
                      )}
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableCell
                            key={header.id}
                            colSpan={header.colSpan}
                            align={"right"}
                            sx={{
                              padding: "10px 8px",
                              backgroundColor: "background.default",
                            }}
                          >
                            {header.isPlaceholder ? null : (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  justifyContent:
                                    header?.column?.columnDef?.align ===
                                      "right" && "flex-end",
                                }}
                                {...{
                                  className: header.column.getCanSort()
                                    ? "cursor-pointer select-none"
                                    : "",
                                  onClick:
                                    header.column.getToggleSortingHandler(),
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    lineHeight: "14px",
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </Typography>

                                {{
                                  asc: (
                                    <img
                                      alt="asc"
                                      src={ascendingIcon}
                                      width={14}
                                      height={14}
                                    />
                                  ),
                                  desc: (
                                    <img
                                      alt="desc"
                                      src={descendingIcon}
                                      width={14}
                                      height={14}
                                    />
                                  ),
                                }[header.column.getIsSorted()] ?? null}
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHead>
              )}
              <TableBody>
                {isLoading || table.getFilteredRowModel().rows.length === 0 ? (
                  isLoading ? (
                    <TableRow>
                      <TableCell align="center" colSpan={5}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : !hideEmptyContent ? (
                    <EmptyComponent emptyTableMessage={emptyTableMessage} />
                  ) : (
                    <></>
                  )
                ) : (
                  table.getRowModel().rows.map((row, index) => {
                    return (
                      <Row
                        key={row.id}
                        index={index}
                        disableDrag={disableDrag}
                        row={row}
                        moveRow={moveRow}
                        tableId={tableId}
                        // {...row.getRowProps()}
                      />
                    );
                  })
                )}
              </TableBody>
            </Table>
            {isPagination && table.getFilteredRowModel().rows.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[8, 10, 20, 30, 40, 50]}
                component="div"
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={table.getState().pagination.pageSize}
                page={table.getState().pagination.pageIndex}
                onPageChange={(e, page) => {
                  table.setPageIndex(page);
                }}
                onRowsPerPageChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              />
            )}
            {tableFooter && <>{tableFooter}</>}
          </TableContainer>
        </DndProvider>
      </Stack>
    </>
  );
};

const TableHeader = ({
  setGlobalFilter,
  headerText,
  rightContent,
  isHeaderStyleChange,
  searchText,
}) => {
  return (
    <Stack
      sx={{
        padding: "16px 12px",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: isHeaderStyleChange && "#111C2B",
        color: isHeaderStyleChange && "#fff",
      }}
    >
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "18px",
        }}
      >
        {headerText}
      </Typography>
      <OutlinedInput
        onChange={(e) => setGlobalFilter(e.target.value)}
        value={searchText}
        sx={{
          height: "36px",
          borderRadius: "10px",
          width: "200px",
          backgroundColor: isHeaderStyleChange ? "#313C4C" : "#F2F7FC",
          borderColor: isHeaderStyleChange && "#485364",
          "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            borderColor: isHeaderStyleChange && "#485364",
          },
          "& input::placeholder": {
            color: isHeaderStyleChange && "#ccc",
          },
          "& input": {
            p: " 12.5px 14px 12.5px 4px !important",
          },
        }}
        placeholder="Search"
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon size="small" />
          </InputAdornment>
        }
        endAdornment={
          searchText && (
            <InputAdornment sx={{ cursor: "pointer" }} position="end">
              <CloseIcon size="large" onClick={(e) => setGlobalFilter("")} />
            </InputAdornment>
          )
        }
      />
      {rightContent}
    </Stack>
  );
};

const EmptyComponent = ({ emptyTableMessage }) => {
  return (
    <TableRow>
      <TableCell
        align="center"
        colSpan={20}
        height={162}
        sx={{ py: 2, padding: "32px 0" }}
      >
        <Stack alignItems="center" gap="8px">
          <img src={emptyIcon} alt="empty" height={56} width={56} />
          <Typography
            sx={{ fontWeight: 400, fontSize: "14px", color: "#485364" }}
          >
            {emptyTableMessage ? emptyTableMessage : "No matches found"}
          </Typography>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const TableHeaderContent = ({ tableHeaderContent }) => {
  return <Box p="8px 16px">{tableHeaderContent}</Box>;
};

const DND_ITEM_TYPE = "row";

const Row = ({ row, index, moveRow, disableDrag, tableId }) => {
  const dropRef = React.useRef(null);
  const dragRef = React.useRef(null);

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
    hover(item, monitor) {
      // Prevent cross-table drops: ensure the drag source table id matches this table
      if (!item || item.tableId !== tableId) return;

      if (!dropRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DND_ITEM_TYPE,
    item: () => ({ index, tableId }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  preview(drop(dropRef));
  drag(dragRef);
  return (
    <TableRow
      ref={dropRef}
      sx={{
        opacity,
        "&:hover": {
          backgroundColor: (theme) => theme.palette.action.hover,
        },
      }}
    >
      {!disableDrag && (
        <TableCell
          padding="normal"
          ref={dragRef}
          sx={{ cursor: "grab" }}
          align="center"
        >
          <DragIndicatorIcon sx={{ height: "20px", width: "20px" }} />
        </TableCell>
      )}
      {row.getVisibleCells().map((cell, index) => {
        return (
          <TableCell
            key={cell.id}
            padding={cell.column.padding || "normal"}
            sx={{
              borderBottom: "1px solid divider",
              p: !cell.column.padding && "8px",
            }}
            align={cell.column.id === "actions" ? "right" : "left"}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
    </TableRow>
  );
};
